import { DropboxFileItem, DropboxFileListLoadedPathsParams } from '@common/types/dropbox';
import { api } from '@services/api';

type GetFirmwareDropboxFilesArgs = {
    path: string;
    page?: number;
    page_size?: number;
    ordering?: string;
};

type ToggleFirmwareFavoriteArgs = {
    path: string;
    folderPath: string;
};

const firmwareDropboxApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getFirmwareDropboxFilesLoadedPaths: builder.query<
            {
                path: string;
                type: 'firmwares';
                results: DropboxFileItem[];
                /**
                 * Remaining count of items that can be loaded for the given path.
                 * This is useful for determining if there are more items to load, in a infinite scroll scenario.
                 * If the count is 0, it means there are no more items to load for the given path.
                 */
                count: number;
            },
            DropboxFileListLoadedPathsParams<'firmwares'>
        >({
            query: (params) => ({
                url: `dropbox/files/`,
                method: 'POST',
                body: {
                    ...params,
                    type: 'firmwares',
                },
            }),
            providesTags: (_res, _err, arg) => [
                {
                    type: 'FirmwaresDropboxFiles',
                    id: `PATH:${arg.path ?? ''}-LOADED_PATHS:${arg.loaded_paths?.join(',') ?? ''}-${
                        arg.page_size ?? ''
                    }-${arg.ordering ?? ''}`,
                },
                {
                    type: 'FirmwaresDropboxFiles',
                    id: `PATH:${arg.path ?? ''}`,
                },
            ],
        }),

        getFirmwareDropboxDownloadJson: builder.query<
            { type: 'firmwares' } & Pick<DropboxFileItem, 'path' | 'download_url' | 'stream_url' | 'url_expires_at'>,
            string
        >({
            query: (path) => ({
                url: `dropbox/download/`,
                params: {
                    path,
                    type: 'firmwares',
                    response_type: 'json',
                },
            }),
        }),
        firmwareFavorite: builder.mutation<void, ToggleFirmwareFavoriteArgs>({
            query: ({ path }) => ({
                url: `dropbox/favorites/`,
                method: 'POST',
                body: { path, type: 'firmwares' },
            }),
            invalidatesTags: (_res, _err, { path, folderPath }) => [
                {
                    type: 'FirmwaresDropboxFiles',
                    id: `PATH:${folderPath ?? ''}`,
                },
            ],
        }),
        firmwareUnfavorite: builder.mutation<void, ToggleFirmwareFavoriteArgs>({
            query: ({ path }) => ({
                url: `dropbox/favorites/`,
                params: { path, type: 'firmwares' },
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { path, folderPath }) => [
                {
                    type: 'FirmwaresDropboxFiles',
                    id: `PATH:${folderPath ?? ''}`,
                },
            ],
        }),
    }),
});

export const {
    useFirmwareFavoriteMutation,
    useFirmwareUnfavoriteMutation,
    useLazyGetFirmwareDropboxFilesLoadedPathsQuery,
    useLazyGetFirmwareDropboxDownloadJsonQuery,
} = firmwareDropboxApi;
export default firmwareDropboxApi;
